import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/*
 * Adaptive countdown (B6 rule): never ambiguous.
 * > 24h  -> days only ("2 days" — 1d23h rounds UP, it reads as 2 days away)
 * 1-24h  -> hours + minutes
 * 1-60m  -> minutes + seconds
 * < 1m   -> seconds
 */

function diffParts(target) {
  const total = Math.max(0, new Date(target).getTime() - Date.now());
  return {
    total,
    days: Math.ceil(total / 86400000),
    hours: Math.floor(total / 3600000),
    hoursMod: Math.floor((total / 3600000) % 24),
    minutes: Math.floor((total / 60000) % 60),
    seconds: Math.floor((total / 1000) % 60),
  };
}

const Unit = ({ value, label, testid, pad = true }) => (
  <div className="flex flex-col items-center gap-1" data-testid={testid}>
    <span className="text-2xl sm:text-3xl font-medium tabular-nums tracking-tight text-[#0A0A0A]">
      {pad ? String(value).padStart(2, "0") : value}
    </span>
    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#0A0A0A]/40">
      {label}
    </span>
  </div>
);

const Sep = () => (
  <span className="text-2xl sm:text-3xl font-light text-[#0A0A0A]/20 select-none" aria-hidden="true">:</span>
);

export const Countdown = ({ target, className = "" }) => {
  const { t } = useTranslation();
  const [parts, setParts] = useState(() => diffParts(target));

  useEffect(() => {
    const interval = setInterval(() => setParts(diffParts(target)), 1000);
    return () => clearInterval(interval);
  }, [target]);

  if (parts.total <= 0) {
    return (
      <span data-testid="countdown-ended" className={`text-sm font-medium text-[#737373] ${className}`}>
        {t("common.ended")}
      </span>
    );
  }

  const overDay = parts.total > 86400000;
  const overHour = parts.total > 3600000;
  const overMinute = parts.total > 60000;

  return (
    <div data-testid="countdown-timer" role="timer" aria-live="off" className={`inline-flex items-start gap-5 ${className}`}>
      {overDay ? (
        <Unit value={parts.days} label={t("common.day", { count: parts.days })} testid="countdown-days" pad={false} />
      ) : overHour ? (
        <>
          <Unit value={parts.hours} label={t("common.hours")} testid="countdown-hours" />
          <Sep />
          <Unit value={parts.minutes} label={t("common.minutes")} testid="countdown-minutes" />
        </>
      ) : overMinute ? (
        <>
          <Unit value={parts.minutes} label={t("common.minutes")} testid="countdown-minutes" />
          <Sep />
          <Unit value={parts.seconds} label={t("common.seconds")} testid="countdown-seconds" />
        </>
      ) : (
        <Unit value={parts.seconds} label={t("common.seconds")} testid="countdown-seconds" />
      )}
    </div>
  );
};
